import {useMemo} from 'react'
import type {NavigateFunction} from 'react-router'
import {useLocation, useNavigate, useParams} from 'react-router'
import {mmap} from '@freckle/maybe'
import {useSafeEffectExtraDeps} from '@freckle/react-hooks'
import {validateDateParam} from '@freckle/educator-entities/ts/common/helpers/date-helper'
import type {DateOption} from '@freckle/educator-entities/ts/common/helpers/date-options'
import {parseDateOption} from '@freckle/educator-entities/ts/common/helpers/date-options'
import {equalExtra, staticExtra} from '@freckle/educator-entities/ts/common/helpers/extra-deps'
import {logErrorAsUnhandled} from '@freckle/classroom/ts/common/helpers/exception-handlers/bugsnag-client'

export type DateParamsT = {
  from: number | null
  to: number | null
  dateOption: DateOption | null
}

export function convertToNumber(str: string | null | undefined): number | null {
  if (str === null || str === undefined || str === '') {
    return null
  }

  // Only parse strings that are purely numeric (no letters, hyphens, etc.)
  if (!/^\d+$/.test(str)) {
    return null
  }

  const mNumber = parseInt(str, 10)
  return isNaN(mNumber) ? null : mNumber
}

export function useStrFromUrlParamKey(key: string): string | null {
  const params = useParams()
  return (params as {[k: string]: string})[key] ?? null
}

export function useNumFromUrlParamKey(key: string): number | null {
  const params = useParams()
  return convertToNumber((params as {[k: string]: string})[key])
}

export function errorIfNullParam<T>(mParam: T | null): T {
  if (mParam === null) {
    // Throwing an `Error` is not great, however, if the param is required and
    // we don't find it, this indicates a development error at a very high-level
    // in a handler (should find early in development rather than prod).
    const err = new Error(`Missing query or path param: ${name}`)
    logErrorAsUnhandled(err)
    throw err
  }

  return mParam
}

export function useRequiredStrValueFromUrlParamKey(key: string): string {
  const mParam = useStrFromUrlParamKey(key)
  return errorIfNullParam(mParam)
}

export function useRequiredNumValueFromUrlParamKey(key: string): number {
  const mParam = useNumFromUrlParamKey(key)
  return errorIfNullParam(mParam)
}

export const useCourseIdQueryParam = (): number | null => useQueryParamAsNum('courseId')
export const useCourseRgpIdQueryParam = (): string | null => useQueryParamAsString('courseRgpId')

export const useStudentIdFromUrlParams = (): number | null => useNumFromUrlParamKey('studentId')

export const useRequiredStudentIdParam = (): number =>
  useRequiredNumValueFromUrlParamKey('studentId')

export const useRequiredAssignmentIdParam = (): number =>
  useRequiredNumValueFromUrlParamKey('assignmentId')

export const useAssignmentSessionIdFromUrlParams = (): number | null =>
  useNumFromUrlParamKey('assignmentSessionId')

export const useArticleUuidFromUrlParams = (): string | null => useStrFromUrlParamKey('articleUuid')

export const useArticleLevelFromUrlParams = (): string | null =>
  useStrFromUrlParamKey('articleLevel')

export function useQuery(): URLSearchParams {
  const {search} = useLocation()
  return useMemo(() => new URLSearchParams(search), [search])
}

export function useUpdateQueryParam(key: string, value: string) {
  const navigate = useNavigate()
  const {search, pathname} = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  useSafeEffectExtraDeps<{
    navigate: NavigateFunction
    searchParams: URLSearchParams
  }>(
    ({navigate, searchParams}) => {
      if (searchParams.get(key) !== value) {
        searchParams.set(key, value)
        navigate({pathname, search: `?${searchParams.toString()}`})
      }
    },
    [pathname, key, value],
    {
      navigate: staticExtra(navigate),
      searchParams: equalExtra(searchParams),
    },
  )
}

export function useUpdateQueryParamFn(key: string): (value: string) => void {
  const navigate = useNavigate()
  const {search, pathname} = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  return (value: string) => {
    if (searchParams.get(key) !== value) {
      searchParams.set(key, value)
      navigate({pathname, search: `?${searchParams.toString()}`})
    }
  }
}

export function useUpdateQueryParamsFn(): (
  updates: Record<string, string | null | undefined>,
) => void {
  const navigate = useNavigate()
  const {search, pathname} = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])

  return (updates: Record<string, string | null | undefined>) => {
    const hasChanges = Object.entries(updates).some(
      ([key, value]) => searchParams.get(key) !== value,
    )
    if (hasChanges) {
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          searchParams.set(key, value)
        } else {
          searchParams.delete(key)
        }
      })
      navigate({pathname, search: `?${searchParams.toString()}`})
    }
  }
}

export function useRemoveQueryParam(key: string) {
  const navigate = useNavigate()
  const {search, pathname} = useLocation()
  const searchParams = useMemo(() => new URLSearchParams(search), [search])
  searchParams.delete(key)
  return () => navigate({pathname, search: `?${searchParams.toString()}`})
}

export function useQueryParamAsString(key: string): string | null {
  const query = useQuery()
  return query.get(key)
}

export function useQueryParamAsNum(key: string): number | null {
  const query = useQuery()
  return convertToNumber(query.get(key))
}

export function useQueryParamAsBoolean(key: string): boolean {
  const query = useQuery()
  return query.has(key)
}

export function useQueryParamAsDate(): DateParamsT {
  const query = useQuery()

  const fromParam = query.get('from')
  const fromMoment = validateDateParam(fromParam)

  const toParam = query.get('to')
  const toMoment = validateDateParam(toParam)

  const dateOptionParam = query.get('date-option')
  const dateOption = mmap(dateOption => parseDateOption(dateOption), dateOptionParam) ?? null

  return {
    from: fromMoment,
    to: toMoment,
    dateOption,
  }
}

const ARRAY_VALUE_SPLIT = '|'

function useQueryParamArray<T>(key: string, callback: (arg: string) => T): Array<T> {
  const query = useQuery()
  const args = mmap(value => value.split(ARRAY_VALUE_SPLIT), query.get(key))
  return (args ?? []).filter((arg: string) => arg.trim().length > 0).map(callback)
}

export function useQueryParamStringArray(key: string): Array<string> {
  return useQueryParamArray(key, a => a)
}

export function useQueryParamNumArray(key: string): Array<number> {
  return useQueryParamArray(key, a => parseInt(a, 10))
}
